import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';

export default function CustomerLoginPage() {
  const navigate = useNavigate();
  const { login } = useCustomerAuth();
  const [customerId, setCustomerId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!customerId.trim() || !password.trim()) {
      setError('Vui lòng nhập đầy đủ mã khách hàng và mật khẩu.');
      return;
    }
    setLoading(true);
    try {
      await login(customerId.trim(), password);
      navigate('/customer/dashboard');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.includes('không tồn tại')) setError('Mã khách hàng không đúng. Vui lòng kiểm tra lại.');
      else if (msg.includes('Mật khẩu')) setError('Mật khẩu không đúng. Vui lòng thử lại.');
      else if (msg.includes('vô hiệu hóa')) setError('Tài khoản đã bị khóa. Vui lòng liên hệ The Daeg.');
      else setError(msg || 'Đăng nhập thất bại. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f6f2] flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 md:px-6 py-3 md:py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/vi" className="flex items-center gap-2.5">
            <div className="w-7 h-7 md:w-8 md:h-8 bg-[#8B6914] rounded-sm flex items-center justify-center shrink-0">
              <span className="text-white font-bold text-xs md:text-sm">D</span>
            </div>
            {/* Desktop: full name */}
            <span className="hidden sm:block font-semibold text-gray-800 text-sm tracking-wide">
              THE DAEG INTERNATIONAL TRADE
            </span>
            {/* Mobile: short name */}
            <span className="sm:hidden font-semibold text-gray-800 text-sm tracking-wide">
              THE DAEG
            </span>
          </a>
          <a
            href="/vi"
            className="text-xs md:text-sm text-gray-500 hover:text-[#8B6914] transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            <span className="hidden sm:inline">Về trang chủ</span>
            <span className="sm:hidden">Trang chủ</span>
          </a>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-4 py-8 md:py-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-100">
            {/* Logo icon */}
            <div className="flex justify-center mb-5 md:mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-[#f5efe3] rounded-full flex items-center justify-center">
                <i className="ri-user-line text-xl md:text-2xl text-[#8B6914]"></i>
              </div>
            </div>

            <h1 className="text-xl md:text-2xl font-bold text-gray-900 text-center mb-1">
              Cổng Thông Tin Khách Hàng
            </h1>
            <p className="text-xs md:text-sm text-gray-500 text-center mb-6 md:mb-8 leading-relaxed">
              Đăng nhập để theo dõi tiến độ hồ sơ định cư của bạn
            </p>

            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5">
              {/* Customer ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mã khách hàng
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-id-card-line text-base"></i>
                  </div>
                  <input
                    type="text"
                    value={customerId}
                    onChange={(e) => setCustomerId(e.target.value)}
                    placeholder="VD: DAEG-2024-001"
                    autoComplete="username"
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#8B6914] focus:ring-1 focus:ring-[#8B6914]/20 transition-all bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Mật khẩu
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400">
                    <i className="ri-lock-line text-base"></i>
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Nhập mật khẩu"
                    autoComplete="current-password"
                    className="w-full pl-10 pr-10 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#8B6914] focus:ring-1 focus:ring-[#8B6914]/20 transition-all bg-gray-50 focus:bg-white"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
                  >
                    {showPassword
                      ? <i className="ri-eye-off-line text-base"></i>
                      : <i className="ri-eye-line text-base"></i>
                    }
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-100 rounded-lg px-3 py-2.5">
                  <div className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-error-warning-line text-red-500 text-sm"></i>
                  </div>
                  <p className="text-xs md:text-sm text-red-600 leading-relaxed">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full bg-[#8B6914] hover:bg-[#7a5c10] text-white font-medium py-3 rounded-lg text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Đang đăng nhập...
                  </span>
                ) : 'Đăng nhập'}
              </button>
            </form>

            {/* Footer note */}
            <div className="mt-5 md:mt-6 pt-5 md:pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-400 text-center leading-relaxed">
                Chưa có tài khoản? Vui lòng liên hệ{' '}
                <a href="/vi/lien-he" className="text-[#8B6914] hover:underline">
                  The Daeg International Trade
                </a>{' '}
                để được cấp mã đăng nhập.
              </p>
            </div>
          </div>

          {/* Help hint — mobile friendly */}
          <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400">
            <i className="ri-information-line text-sm"></i>
            <span>Mã khách hàng được cung cấp qua email khi tạo hồ sơ</span>
          </div>
        </div>
      </main>

      <footer className="py-5 text-center">
        <p className="text-xs text-gray-400">© 2025 The Daeg International Trade. All rights reserved.</p>
      </footer>
    </div>
  );
}
